import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col, Form, Card, Accordion, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { selectPtoEntries } from '../../redux/slices/ptoSlice';
import { selectHolidays } from '../../redux/slices/holidaysSlice';
import { selectProtectedTimeRecords } from '../../redux/slices/protectedTimeSlice';
// Import centralized date utilities
import { formatDateLocalEnhanced, formatDateUnified } from '../../utils/dateUtils';
import { HOURS_PER_DAY, calculateEngineeringDueDate, addBusinessDays, ensureBusinessDay } from '../../utils/engineeringCalculations';

const EngineerAssignmentModal = ({
  show,
  onHide,
  onSelectEngineer,
  engineeringDueDate,
  mechanicalDueDate,
  electricalDueDate,
  controlsDueDate,
  mechanicalEngineers,
  electricalEngineers,
  controlsEngineers,
  selectedJob
}) => {
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const weekData = useSelector((state) => state.weekData);
  const ptoEntries = useSelector(selectPtoEntries);
  const holidays = useSelector(selectHolidays);
  const protectedTimeRecords = useSelector(selectProtectedTimeRecords);
  
  const [loading, setLoading] = useState(true);
  const [engineerWorkloads, setEngineerWorkloads] = useState({
    mechanical: [],
    electrical: [],
    controls: []
  });
  
  // Find Available Engineers feature state
  const [searchingEngineers, setSearchingEngineers] = useState(false);
  const [engineerSearchResults, setEngineerSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Initialize department-specific due dates from selectedJob props
  const [mechanicalDueDateState, setMechanicalDueDateState] = useState(selectedJob?.MechanicalDue || mechanicalDueDate || '');
  const [electricalDueDateState, setElectricalDueDateState] = useState(selectedJob?.ElectricalDue || electricalDueDate || '');
  const [controlsDueDateState, setControlsDueDateState] = useState(selectedJob?.ControlsDue || controlsDueDate || '');
  const [mechanicalDuration, setMechanicalDuration] = useState(selectedJob?.MechanicalDuration || '1');
  const [electricalDuration, setElectricalDuration] = useState(selectedJob?.ElectricalDuration || '1');
  const [controlsDuration, setControlsDuration] = useState(selectedJob?.ControlsDuration || '1');
  
  const [activeEngineerType, setActiveEngineerType] = useState('mechanical');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('engineer'); // 'engineer' or 'week'
  const [startWeek, setStartWeek] = useState(0);
  const [endWeek, setEndWeek] = useState(3); // Default to showing 4 weeks

  const getWeekRangeLabel = (weekNum) => {
    if (!weekData.workWeekColumns || !weekData.workWeekColumns[weekNum]) {
      return `Week ${weekNum + 1}`;
    }
    
    const week = weekData.workWeekColumns[weekNum];
    return `Week ${weekNum + 1} (${week.isoWeekNum}): ${moment(week.startDate).format('MM/DD')} - ${moment(week.endDate).format('MM/DD')}`;
  };

  // Find week index for a given date
  const findWeekForDate = React.useCallback((dateString) => {
    if (!dateString || !weekData.workWeekColumns) return null;
    
    try {
      const date = new Date(dateString);
      
      for (let i = 0; i < weekData.workWeekColumns.length; i++) {
        const weekStart = new Date(weekData.workWeekColumns[i].startDate);
        const weekEnd = new Date(weekData.workWeekColumns[i].endDate);
        
        if (date >= weekStart && date <= weekEnd) {
          return i;
        }
      }
    } catch (error) {
      console.error('Error finding week for date:', error);
    }
    
    return null;
  }, [weekData.workWeekColumns]);

  // Helper function to get PTO entries for a specific engineer
  const getEngineerPtoEntries = (engineerName) => {
    return ptoEntries.filter(entry => entry.UserName === engineerName);
  };
  
  // Helper function to check if a day is within a PTO period
  const isDayInPtoPeriod = (day, ptoEntries) => {
    if (!ptoEntries || !ptoEntries.length) return false;
    
    const dayDate = moment(day).format('YYYY-MM-DD');
    return ptoEntries.some(entry => {
      // Use formatDateUnified to handle UTC dates properly
      const startDate = formatDateUnified(entry.StartDate, 'iso');
      const endDate = formatDateUnified(entry.EndDate, 'iso');
      return dayDate >= startDate && dayDate <= endDate;
    });
  };
  
  // Filter PTO entries by visible date range
  const getVisiblePtoEntries = (engineerName) => {
    const engineerPto = getEngineerPtoEntries(engineerName);
    if (!engineerPto.length) return [];
    
    return engineerPto.filter(entry => {
      if (entry.StartDate && entry.EndDate) {
        // Use formatDateUnified to handle UTC dates properly
        const startDateStr = formatDateUnified(entry.StartDate, 'iso');
        const endDateStr = formatDateUnified(entry.EndDate, 'iso');
        const ptoStartDate = moment(startDateStr);
        const ptoEndDate = moment(endDateStr);
        
        // For week-based filtering
        if (weekData.workWeekColumns && weekData.workWeekColumns.length > 0) {
          const startWeekObj = weekData.workWeekColumns[startWeek];
          const endWeekObj = weekData.workWeekColumns[endWeek];
          
          if (startWeekObj && endWeekObj) {
            const firstDay = moment(startWeekObj.startDate);
            const lastDay = moment(endWeekObj.endDate);
            
            // Check if PTO period overlaps with visible date range
            return (
              (ptoStartDate.isBetween(firstDay, lastDay, 'day', '[]') || 
               ptoEndDate.isBetween(firstDay, lastDay, 'day', '[]')) ||
              (ptoStartDate.isBefore(firstDay) && ptoEndDate.isAfter(lastDay))
            );
          }
        }
      }
      return false;
    });
  };

  // ===== Find Available Engineers Feature =====
  
  /**
   * Check if a day is a weekend
   */
  const isWeekend = (date) => {
    const day = moment(date).day();
    return day === 0 || day === 6;
  };
  
  /**
   * Check if a day is a holiday
   */
  const isDayAHoliday = (date) => {
    if (!holidays || holidays.length === 0) return false;
    const dateStr = moment(date).format('YYYY-MM-DD');
    return holidays.some(holiday => {
      const holidayDateStr = moment(holiday.HolidayDate).format('YYYY-MM-DD');
      return holidayDateStr === dateStr;
    });
  };
  
  /**
   * Get protected time entries for a specific engineer
   */
  const getEngineerProtectedTime = (engineerName) => {
    return protectedTimeRecords.filter(record => record.UserName === engineerName);
  };
  
  /**
   * Calculate protected hours for a single record on a specific day
   */
  const calculateProtectedHoursForRecord = (record, date) => {
    const checkDate = moment(date);
    const dayOfWeek = checkDate.format('dddd');
    
    let selectedDays = [];
    if (record.DayOfWeek) {
      if (Array.isArray(record.DayOfWeek)) {
        selectedDays = record.DayOfWeek;
      } else if (typeof record.DayOfWeek === 'string') {
        try {
          const parsed = JSON.parse(record.DayOfWeek);
          selectedDays = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          if (record.DayOfWeek.includes(',')) {
            selectedDays = record.DayOfWeek.split(',').map(day => day.trim());
          } else {
            selectedDays = [record.DayOfWeek.trim()];
          }
        }
      }
    }
    
    // Full-day blocking (no hours/percent specified)
    if ((!record.HoursPerWeek && !record.WeekPercent) && selectedDays.length > 0) {
      return selectedDays.includes(dayOfWeek) ? HOURS_PER_DAY : 0;
    }
    
    // Calculate hours per week
    let hoursPerWeek = 0;
    if (record.HoursPerWeek) {
      hoursPerWeek = parseFloat(record.HoursPerWeek);
    } else if (record.WeekPercent) {
      hoursPerWeek = (parseFloat(record.WeekPercent) / 100) * 40;
    }
    
    if (hoursPerWeek === 0) return 0;
    
    // Hours/Percent with specific days
    if (selectedDays.length > 0) {
      if (selectedDays.includes(dayOfWeek)) {
        return hoursPerWeek / selectedDays.length;
      }
      return 0;
    }
    
    // Hours/Percent without specific days - distribute across all weekdays
    if (isWeekend(checkDate)) return 0;
    return hoursPerWeek / 5;
  };
  
  /**
   * Get protected time hours for a specific date
   */
  const getProtectedHoursForDay = (date, engineerName) => {
    const protectedRecords = getEngineerProtectedTime(engineerName);
    if (!protectedRecords.length) return 0;
    
    let totalProtectedHours = 0;
    
    protectedRecords.forEach(record => {
      const checkDate = moment(date);
      const hasStartDate = record.StartDate && record.StartDate !== null;
      const hasEndDate = record.EndDate && record.EndDate !== null;
      
      if (hasStartDate && hasEndDate) {
        const startDate = moment(record.StartDate);
        const endDate = moment(record.EndDate);
        if (!checkDate.isBetween(startDate, endDate, 'day', '[]')) {
          return;
        }
      } else if (hasStartDate && !hasEndDate) {
        if (checkDate.isBefore(moment(record.StartDate), 'day')) {
          return;
        }
      } else if (!hasStartDate && hasEndDate) {
        if (checkDate.isAfter(moment(record.EndDate), 'day')) {
          return;
        }
      }
      
      totalProtectedHours += calculateProtectedHoursForRecord(record, date);
    });
    
    return Math.min(totalProtectedHours, HOURS_PER_DAY);
  };
  
  /**
   * Build a complete schedule map for an engineer showing hours allocated per day.
   * Jobs are processed in due date order (earliest first) to ensure priority scheduling.
   * This properly accounts for cumulative hours across all jobs - the sum of all jobs
   * on any given day will not exceed the available hours (after protected time).
   * 
   * IMPORTANT: Hours are allocated FORWARD from the start date, so full days come first
   * and partial days are at the END (near the due date). This allows subsequent jobs
   * to pick up remaining hours on the last day of a previous job.
   * 
   * @param {string} engineerName - The engineer's display name
   * @param {string} engineerType - 'mechanical', 'electrical', or 'controls'
   * @param {number} maxDaysBack - Maximum days to search backward from due dates
   * @returns {Object} Map of date strings to total scheduled hours { 'YYYY-MM-DD': hours }
   */
  const buildEngineerScheduleMap = (engineerName, engineerType, maxDaysBack = 100) => {
    const scheduleMap = {}; // { 'YYYY-MM-DD': totalHoursScheduled }
    
    if (!jobScheduleData || !Array.isArray(jobScheduleData)) return scheduleMap;
    
    // Get field names based on engineer type
    const engineerField = engineerType === 'mechanical' ? 'MechanicalEngineer' : 
                         engineerType === 'electrical' ? 'ElectricalEngineer' : 
                         'ControlsEngineer';
    const doneField = engineerType === 'mechanical' ? 'MechanicalDone' : 
                     engineerType === 'electrical' ? 'ElectricalDone' : 
                     'ControlsDone';
    const dueDateField = engineerType === 'mechanical' ? 'MechanicalDue' : 
                        engineerType === 'electrical' ? 'ElectricalDue' : 
                        'ControlsDue';
    const durationField = engineerType === 'mechanical' ? 'MechanicalDuration' : 
                         engineerType === 'electrical' ? 'ElectricalDuration' : 
                         'ControlsDuration';
    
    // Get all incomplete jobs for this engineer
    const incompleteJobs = jobScheduleData.filter(job => 
      job[engineerField] === engineerName &&
      !job[doneField] &&
      job[dueDateField] &&
      job.JobStatus !== 'Shipped' &&
      !job.ActualShipDate
    );
    
    // Sort by due date (earliest first - these get scheduling priority)
    const sortedJobs = [...incompleteJobs].sort((a, b) => {
      const dateA = moment(a[dueDateField]);
      const dateB = moment(b[dueDateField]);
      return dateA.diff(dateB);
    });
    
    // Process each job and allocate hours, accounting for already-scheduled time
    sortedJobs.forEach(job => {
      const reviewDueDate = moment(job[dueDateField]);
      // Calculate engineering due date by subtracting 2 business days from review due date
      const engineeringDueDate = calculateEngineeringDueDate(reviewDueDate, 2);
      
      const duration = parseFloat(job[durationField]) || 1;
      const totalHoursNeeded = duration * HOURS_PER_DAY;
      
      // STEP 1: Find all available working days from engineering due date backwards
      // Store both the max possible hours (for allocation) and date info
      let currentDay = moment(engineeringDueDate);
      let availableDays = [];
      let daysSearched = 0;
      let potentialHours = 0;
      
      // Collect available days working backwards until we have enough capacity
      while (potentialHours < totalHoursNeeded && daysSearched < maxDaysBack) {
        const dateStr = currentDay.format('YYYY-MM-DD');
        
        if (!isWeekend(currentDay) && 
            !isDayInPtoPeriod(currentDay, getEngineerPtoEntries(engineerName)) &&
            !isDayAHoliday(currentDay)) {
          
          const protectedHours = getProtectedHoursForDay(currentDay, engineerName);
          const maxPossibleHours = HOURS_PER_DAY - protectedHours;
          const alreadyScheduled = scheduleMap[dateStr] || 0;
          const availableHours = Math.max(0, maxPossibleHours - alreadyScheduled);
          
          if (availableHours > 0) {
            availableDays.push({
              date: moment(currentDay),
              dateStr: dateStr,
              maxPossibleHours: maxPossibleHours, // Store max possible for re-checking during allocation
              availableHours: availableHours
            });
            potentialHours += availableHours;
          }
        }
        
        currentDay = currentDay.subtract(1, 'day');
        daysSearched++;
      }
      
      // STEP 2: Reverse the list so we allocate FORWARD (earliest day first)
      // This ensures full days are at the START and partial days at the END
      availableDays.reverse();
      
      // STEP 3: Allocate hours forward through the available days
      // Re-check availability against scheduleMap to prevent any overbooking
      let hoursAllocated = 0;
      for (const day of availableDays) {
        if (hoursAllocated >= totalHoursNeeded) break;
        
        // Re-check current availability (may have changed if same date collected twice somehow)
        const currentlyScheduled = scheduleMap[day.dateStr] || 0;
        const currentlyAvailable = Math.max(0, day.maxPossibleHours - currentlyScheduled);
        
        if (currentlyAvailable > 0) {
          const hoursThisDay = Math.min(currentlyAvailable, totalHoursNeeded - hoursAllocated);
          scheduleMap[day.dateStr] = currentlyScheduled + hoursThisDay;
          hoursAllocated += hoursThisDay;
        }
      }
    });
    
    return scheduleMap;
  };
  
  /**
   * Calculate scheduled hours for an engineer on a specific date.
   * Uses buildEngineerScheduleMap to properly account for cumulative job hours.
   * 
   * @param {moment|Date|string} date - The date to check
   * @param {string} engineerName - The engineer's display name

  
  /**
   * Find earliest available due date for an engineer given a duration.
   * Builds a complete schedule map first to ensure accurate availability checking
   * that accounts for all existing jobs' cumulative hours per day.
   * 
   * IMPORTANT: This function uses a two-phase approach:
   * 1. PHASE 1: Collect all working days with availability until we have enough capacity
   * 2. PHASE 2: Allocate hours with FULL days first, PARTIAL day at the END
   * 
   * A "full availability" day is one where no other jobs are scheduled (only protected time reduces hours).
   * We prefer to start new jobs on full-availability days so partial hours appear at the end.
   */
  const findEarliestAvailableDate = (engineerName, engineerType, durationDays, maxWeeks = 12) => {
    const startDate = moment().startOf('day');
    const endDate = moment().add(maxWeeks, 'weeks');
    const totalHoursNeeded = durationDays * HOURS_PER_DAY;
    
    // Build schedule map once for efficiency - this properly accounts for all existing jobs
    const existingScheduleMap = buildEngineerScheduleMap(engineerName, engineerType);
    
    // PHASE 1: Collect all working days with their availability info
    let currentDay = moment(startDate);
    let availableDays = [];
    let totalPotentialHours = 0;
    let calendarDaysCount = 0;
    const maxDaysToSearch = maxWeeks * 7;
    
    // Collect days until we have enough total capacity
    while (totalPotentialHours < totalHoursNeeded && currentDay.isBefore(endDate) && calendarDaysCount < maxDaysToSearch) {
      // Skip weekends
      if (isWeekend(currentDay)) {
        currentDay = currentDay.add(1, 'day');
        calendarDaysCount++;
        continue;
      }
      
      // Skip PTO
      const engineerPto = getEngineerPtoEntries(engineerName);
      if (isDayInPtoPeriod(currentDay, engineerPto)) {
        currentDay = currentDay.add(1, 'day');
        calendarDaysCount++;
        continue;
      }
      
      // Skip holidays
      if (isDayAHoliday(currentDay)) {
        currentDay = currentDay.add(1, 'day');
        calendarDaysCount++;
        continue;
      }
      
      const dateStr = currentDay.format('YYYY-MM-DD');
      
      // Calculate available capacity for this day
      const protectedHours = getProtectedHoursForDay(currentDay, engineerName);
      const scheduledHours = existingScheduleMap[dateStr] || 0;
      const maxPossibleHours = HOURS_PER_DAY - protectedHours; // Max hours possible on this day
      const availableHours = Math.max(0, maxPossibleHours - scheduledHours);
      
      // Track whether this is a "full" day (no other jobs scheduled)
      const isFullAvailability = scheduledHours === 0 && availableHours > 0;
      
      if (availableHours > 0) {
        availableDays.push({
          date: moment(currentDay),
          dateStr: dateStr,
          availableHours: availableHours,
          maxPossibleHours: maxPossibleHours,
          isFullAvailability: isFullAvailability,
          calendarDay: calendarDaysCount
        });
        totalPotentialHours += availableHours;
      }
      
      currentDay = currentDay.add(1, 'day');
      calendarDaysCount++;
    }
    
    // If we don't have enough capacity, return null
    if (totalPotentialHours < totalHoursNeeded) {
      return null;
    }
    
    // PHASE 2: Find the first "full availability" day to start the job
    // This ensures we don't start on a day with leftover hours from another job
    let startIndex = 0;
    for (let i = 0; i < availableDays.length; i++) {
      if (availableDays[i].isFullAvailability) {
        startIndex = i;
        break;
      }
    }
    
    // Check if starting from the full-availability day gives us enough capacity
    let capacityFromStart = 0;
    for (let i = startIndex; i < availableDays.length; i++) {
      capacityFromStart += availableDays[i].availableHours;
    }
    
    // If not enough capacity from the full-availability day, fall back to starting from day 0
    if (capacityFromStart < totalHoursNeeded) {
      startIndex = 0;
    }
    
    // PHASE 3: Allocate hours forward from the starting point
    // Full days will naturally come first, partial at the end
    let hoursAllocated = 0;
    let workDays = [];
    let workingScheduleMap = { ...existingScheduleMap }; // Track allocations to prevent overbooking
    
    for (let i = startIndex; i < availableDays.length && hoursAllocated < totalHoursNeeded; i++) {
      const day = availableDays[i];
      
      // Re-check available hours using working map (in case we're using overlapping days)
      const currentScheduled = workingScheduleMap[day.dateStr] || 0;
      const currentAvailable = Math.max(0, day.maxPossibleHours - currentScheduled);
      
      if (currentAvailable > 0) {
        const hoursToUse = Math.min(currentAvailable, totalHoursNeeded - hoursAllocated);
        hoursAllocated += hoursToUse;
        
        // Update working schedule map to track this allocation
        workingScheduleMap[day.dateStr] = currentScheduled + hoursToUse;
        
        workDays.push({
          date: moment(day.date),
          hours: hoursToUse,
          availableHours: currentAvailable
        });
      }
    }
    
    // Verify we have enough hours
    if (hoursAllocated < totalHoursNeeded) {
      return null;
    }
    
    // Engineering due date is the last working day
    let engineeringDueDate = moment(workDays[workDays.length - 1].date);
    
    // Ensure engineering due date is not a weekend or holiday
    engineeringDueDate = ensureBusinessDay(engineeringDueDate, holidays);
    
    // Review due date is 2 business days after engineering due date
    const reviewDueDate = addBusinessDays(engineeringDueDate, 2, holidays);
    
    // Find which week this falls into
    const weekIndex = weekData.workWeekColumns?.findIndex(week => {
      const weekStart = moment(week.startDate);
      const weekEnd = moment(week.endDate);
      return reviewDueDate.isBetween(weekStart, weekEnd, 'day', '[]');
    });
    
    // Calculate calendar days span
    const firstWorkDay = workDays[0].date;
    const lastWorkDay = workDays[workDays.length - 1].date;
    const calendarSpan = lastWorkDay.diff(firstWorkDay, 'days') + 1;
    
    return {
      engineeringDueDate: engineeringDueDate.format('YYYY-MM-DD'),
      reviewDueDate: reviewDueDate.format('YYYY-MM-DD'),
      startDate: workDays[0].date.format('YYYY-MM-DD'),
      workDays: workDays,
      weekIndex: weekIndex !== undefined && weekIndex !== -1 ? weekIndex : null,
      calendarDaysSpan: calendarSpan
    };
  };
  
  /**
   * Search all mechanical engineers for earliest availability
   */
  const handleFindAvailableEngineers = async () => {
    setSearchingEngineers(true);
    setShowSearchResults(false);
    
    try {
      const duration = parseFloat(mechanicalDuration) || 1;
      const results = [];
      
      for (const engineer of mechanicalEngineers) {
        const availability = findEarliestAvailableDate(engineer.displayName, 'mechanical', duration, 12);
        
        if (availability) {
          results.push({
            engineer: engineer,
            ...availability
          });
        } else {
          // No availability found within 12 weeks
          results.push({
            engineer: engineer,
            noAvailability: true
          });
        }
      }
      
      // Sort by review due date (earliest first)
      results.sort((a, b) => {
        if (a.noAvailability) return 1;
        if (b.noAvailability) return -1;
        return moment(a.reviewDueDate).diff(moment(b.reviewDueDate));
      });
      
      setEngineerSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error finding available engineers:', error);
    } finally {
      setSearchingEngineers(false);
    }
  };
  
  /**
   * Select an engineer from search results
   */
  const handleSelectFromSearchResults = (result) => {
    if (result.noAvailability) return;
    
    // Update the mechanical due date and duration state
    setMechanicalDueDateState(result.reviewDueDate);
    
    // Close search results
    setShowSearchResults(false);
    
    // Auto-assign the engineer with the calculated due date and duration
    // Pass the values directly to ensure they're used immediately (setState is async)
    // Convert duration to string to match form input format
    const durationValue = String(mechanicalDuration);
    console.log('Assigning engineer from search results:', {
      engineer: result.engineer.displayName,
      dueDate: result.reviewDueDate,
      duration: durationValue,
      dueDateField: 'MechanicalDue',
      durationField: 'MechanicalDuration'
    });
    
    onSelectEngineer('MechanicalEngineer', result.engineer, {
      dueDate: result.reviewDueDate,
      duration: durationValue,
      dueDateField: 'MechanicalDue',
      durationField: 'MechanicalDuration'
    });
    
    onHide(); // Close modal after selection
  };
  
  // ===== End Find Available Engineers Feature =====

  const fetchEngineerWorkloads = useCallback(() => {
    setLoading(true);
    try {
      if (!Array.isArray(jobScheduleData) || jobScheduleData.length === 0) {
        setEngineerWorkloads({
          mechanical: mechanicalEngineers.map(engineer => ({
            ...engineer,
            projectCount: 0,
            projects: [],
            weeklyProjects: {}
          })),
          electrical: electricalEngineers.map(engineer => ({
            ...engineer,
            projectCount: 0,
            projects: [],
            weeklyProjects: {}
          })),
          controls: controlsEngineers.map(engineer => ({
            ...engineer,
            projectCount: 0,
            projects: [],
            weeklyProjects: {}
          }))
        });
        setLoading(false);
        return;
      }

      const activeJobs = jobScheduleData.filter(job => 
        job.JobStatus === "Full Release" && 
        job.ActualShipDate === null
      );
      
      let startDate, endDate;
      
      if (viewMode === 'week' && weekData.workWeekColumns && weekData.workWeekColumns[startWeek] && weekData.workWeekColumns[endWeek]) {
        startDate = new Date(weekData.workWeekColumns[startWeek].startDate);
        endDate = new Date(weekData.workWeekColumns[endWeek].endDate);
      } else {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        endDate = new Date();
        endDate.setDate(endDate.getDate() + 60); // Show 2 months of future work
      }
      
      const mechanicalWorkloads = mechanicalEngineers.map(engineer => {
        const engineerProjects = activeJobs.filter(job => 
          job.MechanicalEngineer === engineer.displayName
        );

        
        const relevantProjects = engineerProjects.filter(job => {
          if (!job.MechanicalDue || !job.EngineeringDue) return false;
          const jobDueDate = new Date(job.MechanicalDue || job.EngineeringDue);
          return jobDueDate >= startDate && jobDueDate <= endDate;
        });

        // Sort projects by due date in descending order (most recent first)
        relevantProjects.sort((a, b) => {
          const dateA = new Date(a.MechanicalDue || a.EngineeringDue);
          const dateB = new Date(b.MechanicalDue || b.EngineeringDue);
          return dateB - dateA; // Descending order
        });

        
        const weeklyProjects = {};
        
        if (weekData.workWeekColumns) {
          weekData.workWeekColumns.forEach((week, weekIdx) => {
            const weekStart = new Date(week.startDate);
            const weekEnd = new Date(week.endDate);
            
            const projectsInWeek = engineerProjects.filter(job => {
              if (!job.MechanicalDue && !job.EngineeringDue) return false;
              const jobDueDate = new Date(job.MechanicalDue || job.EngineeringDue);
              return jobDueDate >= weekStart && jobDueDate <= weekEnd;
            });
            
            // Sort weekly projects by due date in descending order
            projectsInWeek.sort((a, b) => {
              const dateA = new Date(a.MechanicalDue || a.EngineeringDue);
              const dateB = new Date(b.MechanicalDue || b.EngineeringDue);
              return dateB - dateA;
            });
            
            weeklyProjects[weekIdx] = projectsInWeek.map(job => ({
              JobNumber: job.JobNumber || 'N/A',
              ProjectName: job.ProjectName || 'N/A',
              ModelNumber: job.ModelNumber || 'N/A',
              EngineeringDue: job.MechanicalDue || job.EngineeringDue || 'N/A',
              Duration: job.MechanicalDuration || '1'
            }));
          });
        }
        
        return {
          ...engineer,
          projectCount: relevantProjects.length,
          totalProjectCount: engineerProjects.length,
          projects: relevantProjects.map(job => ({
            JobNumber: job.JobNumber || 'N/A',
            ProjectName: job.ProjectName || 'N/A',
            ModelNumber: job.ModelNumber || 'N/A',
            EngineeringDue: job.MechanicalDue || job.EngineeringDue || 'N/A',
            Duration: job.MechanicalDuration || '1'
          })),
          weeklyProjects
        };
      });
      
      const electricalWorkloads = electricalEngineers.map(engineer => {
        const engineerProjects = activeJobs.filter(job => 
          job.ElectricalEngineer === engineer.displayName
        );
        
        const relevantProjects = engineerProjects.filter(job => {
          if (!job.ElectricalDue && !job.EngineeringDue) return false;
          const jobDueDate = new Date(job.ElectricalDue || job.EngineeringDue);
          return jobDueDate >= startDate && jobDueDate <= endDate;
        });
        
        // Sort projects by due date in descending order (most recent first)
        relevantProjects.sort((a, b) => {
          const dateA = new Date(a.ElectricalDue || a.EngineeringDue);
          const dateB = new Date(b.ElectricalDue || b.EngineeringDue);
          return dateB - dateA; // Descending order
        });
        
        const weeklyProjects = {};
        
        if (weekData.workWeekColumns) {
          weekData.workWeekColumns.forEach((week, weekIdx) => {
            const weekStart = new Date(week.startDate);
            const weekEnd = new Date(week.endDate);
            
            const projectsInWeek = engineerProjects.filter(job => {
              if (!job.ElectricalDue && !job.EngineeringDue) return false;
              const jobDueDate = new Date(job.ElectricalDue || job.EngineeringDue);
              return jobDueDate >= weekStart && jobDueDate <= weekEnd;
            });
            
            // Sort weekly projects by due date in descending order
            projectsInWeek.sort((a, b) => {
              const dateA = new Date(a.ElectricalDue || a.EngineeringDue);
              const dateB = new Date(b.ElectricalDue || b.EngineeringDue);
              return dateB - dateA;
            });
            
            weeklyProjects[weekIdx] = projectsInWeek.map(job => ({
              JobNumber: job.JobNumber || 'N/A',
              ProjectName: job.ProjectName || 'N/A',
              ModelNumber: job.ModelNumber || 'N/A',
              EngineeringDue: job.ElectricalDue || job.EngineeringDue || 'N/A',
              Duration: job.ElectricalDuration || '1'
            }));
          });
        }
        
        return {
          ...engineer,
          projectCount: relevantProjects.length,
          totalProjectCount: engineerProjects.length,
          projects: relevantProjects.map(job => ({
            JobNumber: job.JobNumber || 'N/A',
            ProjectName: job.ProjectName || 'N/A',
            ModelNumber: job.ModelNumber || 'N/A',
            EngineeringDue: job.ElectricalDue || job.EngineeringDue || 'N/A',
            Duration: job.ElectricalDuration || '1'
          })),
          weeklyProjects
        };
      });
      
      const controlsWorkloads = controlsEngineers.map(engineer => {
        const engineerProjects = activeJobs.filter(job => 
          job.ControlsEngineer === engineer.displayName
        );
        
        const relevantProjects = engineerProjects.filter(job => {
          if (!job.ControlsDue && !job.EngineeringDue) return false;
          const jobDueDate = new Date(job.ControlsDue || job.EngineeringDue);
          return jobDueDate >= startDate && jobDueDate <= endDate;
        });
        
        // Sort projects by due date in descending order (most recent first)
        relevantProjects.sort((a, b) => {
          const dateA = new Date(a.ControlsDue || a.EngineeringDue);
          const dateB = new Date(b.ControlsDue || b.EngineeringDue);
          return dateB - dateA; // Descending order
        });
        
        const weeklyProjects = {};
        
        if (weekData.workWeekColumns) {
          weekData.workWeekColumns.forEach((week, weekIdx) => {
            const weekStart = new Date(week.startDate);
            const weekEnd = new Date(week.endDate);
            
            const projectsInWeek = engineerProjects.filter(job => {
              if (!job.ControlsDue && !job.EngineeringDue) return false;
              const jobDueDate = new Date(job.ControlsDue || job.EngineeringDue);
              return jobDueDate >= weekStart && jobDueDate <= weekEnd;
            });
            
            // Sort weekly projects by due date in descending order
            projectsInWeek.sort((a, b) => {
              const dateA = new Date(a.ControlsDue || a.EngineeringDue);
              const dateB = new Date(b.ControlsDue || b.EngineeringDue);
              return dateB - dateA;
            });
            
            weeklyProjects[weekIdx] = projectsInWeek.map(job => ({
              JobNumber: job.JobNumber || 'N/A',
              ProjectName: job.ProjectName || 'N/A',
              ModelNumber: job.ModelNumber || 'N/A',
              EngineeringDue: job.ControlsDue || job.EngineeringDue || 'N/A',
              Duration: job.ControlsDuration || '1'
            }));
          });
        }
        
        return {
          ...engineer,
          projectCount: relevantProjects.length,
          totalProjectCount: engineerProjects.length,
          projects: relevantProjects.map(job => ({
            JobNumber: job.JobNumber || 'N/A',
            ProjectName: job.ProjectName || 'N/A',
            ModelNumber: job.ModelNumber || 'N/A',
            EngineeringDue: job.ControlsDue || job.EngineeringDue || 'N/A',
            Duration: job.ControlsDuration || '1'
          })),
          weeklyProjects
        };
      });
      
      setEngineerWorkloads({
        mechanical: mechanicalWorkloads,
        electrical: electricalWorkloads,
        controls: controlsWorkloads
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error processing engineer workloads:', error);
      setLoading(false);
    }
  }, [jobScheduleData, mechanicalEngineers, electricalEngineers, controlsEngineers, viewMode, weekData, startWeek, endWeek]);

  useEffect(() => {
    if (show) {
      fetchEngineerWorkloads();
    }
  }, [show, mechanicalDueDateState, electricalDueDateState, controlsDueDateState, fetchEngineerWorkloads]);

  useEffect(() => {
    if (!show) {
      setSearchTerm('');
    }
  }, [show]);

  useEffect(() => {
    if (selectedJob) {
      setMechanicalDueDateState(selectedJob.MechanicalDue || mechanicalDueDate || '');
      setElectricalDueDateState(selectedJob.ElectricalDue || electricalDueDate || '');
      setControlsDueDateState(selectedJob.ControlsDue || controlsDueDate || '');
      setMechanicalDuration(selectedJob.MechanicalDuration || '1');
      setElectricalDuration(selectedJob.ElectricalDuration || '1');
      setControlsDuration(selectedJob.ControlsDuration || '1');
    }
  }, [selectedJob, mechanicalDueDate, electricalDueDate, controlsDueDate]);

  // Update due date weeks when due dates change
  useEffect(() => {
    const mechanicalWeek = findWeekForDate(mechanicalDueDateState);
    const electricalWeek = findWeekForDate(electricalDueDateState);
    const controlsWeek = findWeekForDate(controlsDueDateState);
    
    // Automatically set the start and end week when switching to week view
    // based on the due date of the currently selected engineer type
    if (viewMode === 'week') {
      const currentDueWeek = 
        activeEngineerType === 'mechanical' ? mechanicalWeek :
        activeEngineerType === 'electrical' ? electricalWeek :
        activeEngineerType === 'controls' ? controlsWeek : null;
        
      if (currentDueWeek !== null) {
        const weekStart = Math.max(0, currentDueWeek - 1);
        const weekEnd = Math.min(
          (weekData.workWeekColumns?.length || 0) - 1, 
          currentDueWeek + 1
        );
        
        setStartWeek(weekStart);
        setEndWeek(weekEnd);
      }
    }
  }, [mechanicalDueDateState, electricalDueDateState, controlsDueDateState, viewMode, activeEngineerType, weekData.workWeekColumns, findWeekForDate]);

  const handleSelectEngineer = (engineerType, engineer) => {
    const fieldName = engineerType === 'mechanical' ? 'MechanicalEngineer' : 
                     engineerType === 'electrical' ? 'ElectricalEngineer' : 
                     'ControlsEngineer';
    
    const dueDate = engineerType === 'mechanical' ? mechanicalDueDateState : 
                   engineerType === 'electrical' ? electricalDueDateState : 
                   controlsDueDateState;
                   
    const duration = engineerType === 'mechanical' ? mechanicalDuration : 
                    engineerType === 'electrical' ? electricalDuration : 
                    controlsDuration;
    
    onSelectEngineer(fieldName, engineer, {
      dueDate: dueDate,
      duration: duration,
      dueDateField: engineerType === 'mechanical' ? 'MechanicalDue' : 
                   engineerType === 'electrical' ? 'ElectricalDue' : 
                   'ControlsDue',
      durationField: engineerType === 'mechanical' ? 'MechanicalDuration' : 
                    engineerType === 'electrical' ? 'ElectricalDuration' : 
                    'ControlsDuration'
    });
    
    onHide(); // Close modal after selection
  };

  const handleMechanicalDueDateChange = (e) => {
    setMechanicalDueDateState(e.target.value);
  };

  const handleElectricalDueDateChange = (e) => {
    setElectricalDueDateState(e.target.value);
  };

  const handleControlsDueDateChange = (e) => {
    setControlsDueDateState(e.target.value);
  };

  const handleMechanicalDurationChange = (e) => {
    setMechanicalDuration(e.target.value);
  };

  const handleElectricalDurationChange = (e) => {
    setElectricalDuration(e.target.value);
  };

  const handleControlsDurationChange = (e) => {
    setControlsDuration(e.target.value);
  };

  const filteredEngineers = (type) => {
    const engineers = engineerWorkloads[type] || [];
    if (!searchTerm) return engineers;
    
    return engineers.filter(engineer => 
      engineer.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getWorkloadBadgeVariant = (count) => {
    if (count === 0) return 'success';
    if (count < 3) return 'info';
    if (count < 5) return 'warning';
    return 'danger';
  };
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    return formatDateLocalEnhanced(dateString);
  };
  
  const renderEngineerDueForm = () => {
    const getFormFields = () => {
      switch (activeEngineerType) {
        case 'mechanical':
          return (
            <>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mechanical Design Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={mechanicalDueDateState}
                      onChange={handleMechanicalDueDateChange}
                    />
                    <Form.Text className="text-muted">
                      Date when mechanical design work should be completed
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (days)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="30"
                      step="0.25"
                      value={mechanicalDuration}
                      onChange={handleMechanicalDurationChange}
                    />
                    <Form.Text className="text-muted">
                      Estimated days
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Find Available Engineers Button */}
              <Row className="mb-3">
                <Col>
                  <Button 
                    variant="success" 
                    size="sm"
                    onClick={handleFindAvailableEngineers}
                    disabled={searchingEngineers || !mechanicalDuration}
                  >
                    {searchingEngineers ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Searching Engineers...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-2"></i>
                        Find Available Engineers
                      </>
                    )}
                  </Button>
                  <Form.Text className="text-muted ms-2">
                    Search for engineers with earliest availability based on current duration
                  </Form.Text>
                </Col>
              </Row>
              
              {/* Search Results */}
              {showSearchResults && engineerSearchResults.length > 0 && (
                <Row className="mb-3">
                  <Col>
                    <Card className="border-success">
                      <Card.Header className="bg-success text-white">
                        <strong>Available Engineers (Next 12 Weeks)</strong>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <ListGroup variant="flush">
                          {engineerSearchResults.map((result, idx) => (
                            <ListGroup.Item 
                              key={idx}
                              action={!result.noAvailability}
                              onClick={() => !result.noAvailability && handleSelectFromSearchResults(result)}
                              className={!result.noAvailability ? 'cursor-pointer' : ''}
                            >
                              {result.noAvailability ? (
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{result.engineer.displayName}</strong>
                                    <Badge bg="danger" className="ms-2">No Availability</Badge>
                                  </div>
                                  <small className="text-muted">Fully booked for next 12 weeks</small>
                                </div>
                              ) : (
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                      <strong>{result.engineer.displayName}</strong>
                                      {idx === 0 && <Badge bg="primary" className="ms-2">Earliest</Badge>}
                                    </div>
                                    <div className="text-end">
                                      <div>
                                        <small className="text-muted">Design Due:</small>{' '}
                                        <strong>{moment(result.engineeringDueDate).format('MMM D, YYYY')}</strong>
                                      </div>
                                      <div>
                                        <small className="text-muted">Engineering Due:</small>{' '}
                                        <strong>{moment(result.reviewDueDate).format('MMM D, YYYY')}</strong>
                                      </div>
                                      {result.weekIndex !== null && weekData.workWeekColumns?.[result.weekIndex] && (
                                        <Badge bg="info" className="mt-1">
                                          Week {result.weekIndex + 1} ({weekData.workWeekColumns[result.weekIndex].isoWeekNum})
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Mini Timeline */}
                                  <div className="mt-2 p-2 bg-light rounded">
                                    <small className="text-muted d-block mb-1">
                                      <i className="bi bi-calendar-range me-1"></i>
                                      Work Schedule: {moment(result.startDate).format('MMM D')} - {moment(result.engineeringDueDate).format('MMM D')} 
                                      ({result.calendarDaysSpan} calendar days, {result.workDays.length} working days)
                                    </small>
                                    <div className="d-flex gap-1 flex-wrap">
                                      {result.workDays.slice(0, 10).map((day, dayIdx) => (
                                        <div
                                          key={dayIdx}
                                          className="text-center"
                                          style={{
                                            minWidth: '40px',
                                            fontSize: '10px'
                                          }}
                                        >
                                          <div 
                                            className="p-1 rounded"
                                            style={{
                                              backgroundColor: day.hours >= HOURS_PER_DAY ? '#28a745' : '#ffc107',
                                              color: 'white',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {day.hours.toFixed(1)}h
                                          </div>
                                          <div className="text-muted" style={{fontSize: '9px'}}>
                                            {day.date.format('M/D')}
                                          </div>
                                        </div>
                                      ))}
                                      {result.workDays.length > 10 && (
                                        <div className="text-muted align-self-center">
                                          +{result.workDays.length - 10} more days
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          );
        case 'electrical':
          return (
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Electrical Design Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={electricalDueDateState}
                    onChange={handleElectricalDueDateChange}
                  />
                  <Form.Text className="text-muted">
                    Date when electrical engineering work should be completed
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration (days)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="30"
                    value={electricalDuration}
                    onChange={handleElectricalDurationChange}
                  />
                  <Form.Text className="text-muted">
                    Estimated days
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          );
        case 'controls':
          return (
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Controls Design Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={controlsDueDateState}
                    onChange={handleControlsDueDateChange}
                  />
                  <Form.Text className="text-muted">
                    Date when controls engineering work should be completed
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration (days)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="30"
                    value={controlsDuration}
                    onChange={handleControlsDurationChange}
                  />
                  <Form.Text className="text-muted">
                    Estimated days
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          );
        default:
          return null;
      }
    };

    return (
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <strong>Design Due Dates</strong>
        </Card.Header>
        <Card.Body>
          <Form>
            {getFormFields()}
          </Form>
        </Card.Body>
      </Card>
    );
  };

  const renderEngineerView = () => {
    return (
      <>
        <div className="d-flex justify-content-start mb-3">
          <Button
            variant={activeEngineerType === 'mechanical' ? 'primary' : 'outline-primary'}
            className="me-2"
            onClick={() => setActiveEngineerType('mechanical')}
          >
            Mechanical Engineers
          </Button>
          <Button
            variant={activeEngineerType === 'electrical' ? 'primary' : 'outline-primary'}
            className="me-2"
            onClick={() => setActiveEngineerType('electrical')}
          >
            Electrical Engineers
          </Button>
          <Button
            variant={activeEngineerType === 'controls' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveEngineerType('controls')}
          >
            Controls Engineers
          </Button>
        </div>
        
        {renderEngineerDueForm()}
        
        <div className="engineer-list">
          <Accordion flush>
            {filteredEngineers(activeEngineerType).map((engineer, index) => (
              <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <span>
                      {engineer.displayName}
                    </span>
                    <div>
                      <Badge bg={getWorkloadBadgeVariant(engineer.projectCount)} pill className="me-2">
                        {engineer.projectCount} project{engineer.projectCount !== 1 ? 's' : ''} in selected range
                      </Badge>
                      <Badge bg="secondary" pill>
                        {engineer.totalProjectCount} total project{engineer.totalProjectCount !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {/* Display PTO entries at the top of each engineer's section */}
                  {(() => {
                    const engineerPto = getEngineerPtoEntries(engineer.displayName);
                    if (engineerPto.length > 0) {
                      // Filter PTO entries by visible date range
                      const visiblePto = getVisiblePtoEntries(engineer.displayName);
                      
                      if (visiblePto.length > 0) {
                        return (
                          <div className="mb-3 p-2 bg-light border-danger border-start border-4 rounded">
                            <h6><i className="bi bi-calendar-x me-2"></i>Scheduled PTO</h6>
                            {visiblePto.map((pto, idx) => (
                              <div key={`engineer-pto-${pto.id}`} className="mb-1">
                                <Badge bg="danger" className="me-1">PTO</Badge>
                                <strong>{formatDateUnified(pto.StartDate, 'display')} - {formatDateUnified(pto.EndDate, 'display')}</strong>
                                {pto.Notes && <span className="ms-1">({pto.Notes})</span>}
                              </div>
                            ))}
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                  
                  {engineer.projects.length > 0 ? (
                    <ListGroup variant="flush">
                      {engineer.projects
                        .sort((a, b) => {
                          // Sort by due date in ascending order (earliest first)
                          // Change to `dateB - dateA` for descending order (latest first)
                          const dateA = new Date(a.EngineeringDue);
                          const dateB = new Date(b.EngineeringDue);
                          return dateA - dateB; // Ascending order (earliest due date first)
                        })
                        .map((project, idx) => (
                        <ListGroup.Item key={idx} className="px-0">
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>{project.JobNumber}</strong> - {project.ProjectName}
                            </div>
                            <div>
                              Due: {formatDate(project.EngineeringDue)}
                              <span className="ms-2 badge bg-info">{project.Duration || '1'} days</span>
                            </div>
                          </div>
                          <small className="text-muted">{project.ModelNumber}</small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p className="text-muted">No active projects in the selected date range.</p>
                  )}
                  
                  <div className="d-flex justify-content-end mt-3">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectEngineer(activeEngineerType, engineer);
                      }}
                    >
                      Assign to {activeEngineerType === 'mechanical' ? 'Mechanical' : activeEngineerType === 'electrical' ? 'Electrical' : 'Controls'}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          
          {filteredEngineers(activeEngineerType).length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No engineers found matching your search.</p>
            </div>
          )}
        </div>
      </>
    );
  };
  
  const renderWeekView = () => {
    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={2}>Select Start Week:</Form.Label>
          <Col sm={4}>
            <Form.Select
              value={startWeek}
              onChange={(e) => setStartWeek(parseInt(e.target.value, 10))}
            >
              {weekData.workWeekColumns?.map((week, index) => (
                <option key={index} value={index}>
                  {getWeekRangeLabel(index)}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Form.Label column sm={2}>Select End Week:</Form.Label>
          <Col sm={4}>
            <Form.Select
              value={endWeek}
              onChange={(e) => setEndWeek(parseInt(e.target.value, 10))}
            >
              {weekData.workWeekColumns?.map((week, index) => (
                <option key={index} value={index}>
                  {getWeekRangeLabel(index)}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Form.Group>
        
        <div className="d-flex justify-content-start mb-3">
          <Button
            variant={activeEngineerType === 'mechanical' ? 'primary' : 'outline-primary'}
            className="me-2"
            onClick={() => setActiveEngineerType('mechanical')}
          >
            Mechanical Engineers
          </Button>
          <Button
            variant={activeEngineerType === 'electrical' ? 'primary' : 'outline-primary'}
            className="me-2"
            onClick={() => setActiveEngineerType('electrical')}
          >
            Electrical Engineers
          </Button>
          <Button
            variant={activeEngineerType === 'controls' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveEngineerType('controls')}
          >
            Controls Engineers
          </Button>
        </div>
        
        {renderEngineerDueForm()}
        
        <Accordion flush>
          {filteredEngineers(activeEngineerType).map((engineer, index) => {
            const weeklyProjectCount = engineer.weeklyProjects && 
              Object.keys(engineer.weeklyProjects).reduce((total, weekIdx) => {
                if (weekIdx >= startWeek && weekIdx <= endWeek) {
                  return total + engineer.weeklyProjects[weekIdx].length;
                }
                return total;
              }, 0);
              
            return (
              <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <span>
                      {engineer.displayName}
                    </span>
                    <div>
                      <Badge bg={getWorkloadBadgeVariant(weeklyProjectCount)} pill className="me-2">
                        {weeklyProjectCount} project{weeklyProjectCount !== 1 ? 's' : ''} in selected weeks
                      </Badge>
                      <Badge bg="secondary" pill>
                        {engineer.totalProjectCount} total project{engineer.totalProjectCount !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {weekData.workWeekColumns?.slice(startWeek, endWeek + 1).map((week, weekIdx) => {
                    const actualWeekIdx = startWeek + weekIdx;
                    const projectsInWeek = engineer.weeklyProjects?.[actualWeekIdx] || [];
                    
                    // Get PTO entries for this engineer in this week
                    const ptoEntriesForWeek = (() => {
                      const engineerPto = getEngineerPtoEntries(engineer.displayName);
                      if (engineerPto.length > 0) {
                        // Check for PTO that overlaps with this week's dates
                        const weekStart = moment(week.startDate);
                        const weekEnd = moment(week.endDate);
                        
                        return engineerPto.filter(entry => {
                          if (entry.StartDate && entry.EndDate) {
                            // Use formatDateUnified to handle UTC dates properly
                            const startDateStr = formatDateUnified(entry.StartDate, 'iso');
                            const endDateStr = formatDateUnified(entry.EndDate, 'iso');
                            const ptoStartDate = moment(startDateStr);
                            const ptoEndDate = moment(endDateStr);
                            
                            // Check if PTO period overlaps with this week
                            return (
                              (ptoStartDate.isBetween(weekStart, weekEnd, 'day', '[]') || 
                              ptoEndDate.isBetween(weekStart, weekEnd, 'day', '[]')) ||
                              (ptoStartDate.isBefore(weekStart) && ptoEndDate.isAfter(weekEnd))
                            );
                          }
                          return false;
                        });
                      }
                      return [];
                    })();
                    
                    return (
                      <div key={weekIdx} className="mb-3">
                        <h6>
                          {getWeekRangeLabel(actualWeekIdx)}
                          {projectsInWeek.length > 0 && (
                            <Badge bg={getWorkloadBadgeVariant(projectsInWeek.length)} className="ms-2">
                              {projectsInWeek.length} project{projectsInWeek.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </h6>
                        
                        {/* Display PTO entries for this week */}
                        {ptoEntriesForWeek.length > 0 && (
                          <div className="mb-3 p-2 bg-light border-danger border-start border-4 rounded">
                            <h6 className="mb-2"><i className="bi bi-calendar-x me-2"></i>PTO in this week</h6>
                            {ptoEntriesForWeek.map((pto, idx) => (
                              <div key={`week-pto-${pto.id}-${idx}`} className="mb-1">
                                <Badge bg="danger" className="me-1">PTO</Badge>
                                <strong>{formatDateUnified(pto.StartDate, 'display')} - {formatDateUnified(pto.EndDate, 'display')}</strong>
                                {pto.Notes && <span className="ms-1">({pto.Notes})</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {projectsInWeek.length > 0 ? (
                          <ListGroup variant="flush">
                            {projectsInWeek.map((project, idx) => (
                              <ListGroup.Item key={idx} className="px-0">
                                <div className="d-flex justify-content-between">
                                  <div>
                                    <strong>{project.JobNumber}</strong> - {project.ProjectName}
                                  </div>
                                  <div>
                                    Due: {formatDate(project.EngineeringDue)}
                                    <span className="ms-2 badge bg-info">{project.Duration || '1'} days</span>
                                  </div>
                                </div>
                                <small className="text-muted">{project.ModelNumber}</small>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <p className="text-muted">No projects scheduled for this week.</p>
                        )}
                      </div>
                    );
                  })}
                  
                  <div className="d-flex justify-content-end mt-3">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectEngineer(activeEngineerType, engineer);
                      }}
                    >
                      Assign to {activeEngineerType === 'mechanical' ? 'Mechanical' : activeEngineerType === 'electrical' ? 'Electrical' : 'Controls'}
                    </Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
        
        {filteredEngineers(activeEngineerType).length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">No engineers found matching your search.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal 
      className="job-schedule-modal"
      show={show} 
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Engineer Assignment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col>
            <p>
              {viewMode === 'engineer' && (
                <span>
                  Showing engineer workloads for projects due around: <strong>
                    {activeEngineerType === 'mechanical' && mechanicalDueDateState ? 
                      new Date(mechanicalDueDateState).toLocaleDateString() : 
                     activeEngineerType === 'electrical' && electricalDueDateState ? 
                      new Date(electricalDueDateState).toLocaleDateString() : 
                     activeEngineerType === 'controls' && controlsDueDateState ? 
                      new Date(controlsDueDateState).toLocaleDateString() : 
                      'No date selected'}
                  </strong>
                </span>
              )}
              {viewMode === 'week' && (
                <span>
                  Showing engineer workloads by week
                </span>
              )}
            </p>
          </Col>
        </Row>
        
        <Row className="mb-3">
          <Col sm={8}>
            <Form.Control 
              type="text" 
              placeholder="Search engineers by name..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col sm={4}>
            <div className="d-flex justify-content-end">
              <Button 
                variant={viewMode === 'week' ? 'primary' : 'outline-primary'}
                className="me-2"
                onClick={() => {
                  setViewMode('week');
                }}
              >
                Week View
              </Button>
              <Button 
                variant={viewMode === 'engineer' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setViewMode('engineer');
                }}
              >
                Engineer View
              </Button>
            </div>
          </Col>
        </Row>
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading engineer workloads...</p>
          </div>
        ) : (
          <>
            {viewMode === 'engineer' ? renderEngineerView() : renderWeekView()}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EngineerAssignmentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSelectEngineer: PropTypes.func.isRequired,
  engineeringDueDate: PropTypes.string,
  mechanicalDueDate: PropTypes.string,
  electricalDueDate: PropTypes.string,
  controlsDueDate: PropTypes.string,
  mechanicalEngineers: PropTypes.array.isRequired,
  electricalEngineers: PropTypes.array.isRequired,
  controlsEngineers: PropTypes.array.isRequired,
  selectedJob: PropTypes.object
};

export default EngineerAssignmentModal;
